import {
  createContext,
  Reducer,
  ReducerState,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { UpdateWorkerActions } from '@company/state/useUpdateWorker/actions';
import * as workerApi from '@company/services/worker/api';
import { InfiniteData, QueryClient } from '@tanstack/react-query';
import ITalentsData from '@company/types/talents.type';
import reducer, {
  updateWorkerInitialState,
  UpdateWorkerModel,
} from './reducers';

interface FetchUpdatedWorkerProps {
  data: InfiniteData<ITalentsData, unknown> | undefined;
  queryClient: QueryClient;
  queryKey: (string | number | undefined)[];
}
interface IUpdateWorkerContext {
  updateWorker: (
    payload: UpdateWorkerModel,
    refetchJobAssignment?: () => Promise<any>
  ) => Promise<void>;
  manuallyUpdateWorker: (payload: UpdateWorkerModel) => Promise<void>;
  updatedWorker: UpdateWorkerModel;
  fetchUpdatedWorker: (payload: FetchUpdatedWorkerProps) => Promise<void>;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const UpdateWorkerContext: IUpdateWorkerContext | any = createContext(
  updateWorkerInitialState
);

type UpdateWorkerModelState = UpdateWorkerModel & Reducer<any, any>;

function useUpdateWorkerReducer() {
  const [updatedWorker, dispatch] = useReducer(
    reducer,
    updateWorkerInitialState as ReducerState<UpdateWorkerModelState>
  );

  const updateWorker = async (
    payload: UpdateWorkerModel,
    refetchJobAssignment?: () => Promise<any>
  ) => {
    dispatch({ type: UpdateWorkerActions.UPDATING });
    try {
      const workerProfile = await workerApi.fetchWorkerJobInfo(
        payload.workerId,
        payload.jobId,
        false
      );
      const value = { ...payload, workerProfile };
      dispatch({ type: UpdateWorkerActions.UPDATE, value });
      dispatch({ type: UpdateWorkerActions.UPDATED });

      // Also refetch the job assignment context if provided
      if (refetchJobAssignment) {
        await refetchJobAssignment();
      }
    } catch (err) {
      dispatch({ type: UpdateWorkerActions.UPDATE_FAILED });
    }
  };

  const manuallyUpdateWorker = async (payload: UpdateWorkerModel) => {
    dispatch({ type: UpdateWorkerActions.UPDATING });
    dispatch({ type: UpdateWorkerActions.UPDATE, value: payload });
    dispatch({ type: UpdateWorkerActions.UPDATED });
  };

  return {
    updateWorker,
    updatedWorker,
    manuallyUpdateWorker,
  };
}

export function UpdateWorkerProvider({ children }: ProviderProps) {
  const { updateWorker, updatedWorker, manuallyUpdateWorker } =
    useUpdateWorkerReducer();

  // TODO: change to better named function, maybe refreshUpdatedWorker
  const fetchUpdatedWorker = useCallback(
    async (payload: FetchUpdatedWorkerProps) => {
      const { data, queryClient, queryKey } = payload;
      if (data?.pages && updatedWorker?.workerProfile) {
        const updatedPages = data.pages.map((page) => {
          if (!page.results) {
            return {};
          }
          const updatedResults = page.results.map((result) => {
            const updatedResult = { ...result };
            if (
              updatedWorker.workerProfile &&
              result.worker.id === updatedWorker?.workerId
            ) {
              updatedResult.worker = updatedWorker.workerProfile.worker;
              updatedResult.jobAssignment =
                updatedWorker.workerProfile.jobAssignment!;
              updatedResult.jobPlacement =
                updatedWorker.workerProfile.jobPlacement!;
            }
            return updatedResult;
          });
          return { ...page, results: updatedResults };
        });
        // Fix: Handle both array and string query keys properly
        const normalizedQueryKey = Array.isArray(queryKey)
          ? queryKey
          : [queryKey];
        queryClient.setQueryData(normalizedQueryKey, {
          ...data,
          pages: updatedPages,
        });
      }
    },
    [updatedWorker?.workerId, updatedWorker.workerProfile]
  );

  const contextValue: IUpdateWorkerContext = useMemo(() => {
    return {
      updateWorker,
      updatedWorker,
      fetchUpdatedWorker,
      manuallyUpdateWorker,
    };
  }, [updateWorker, updatedWorker, fetchUpdatedWorker, manuallyUpdateWorker]);

  return (
    <UpdateWorkerContext.Provider value={contextValue}>
      {children}
    </UpdateWorkerContext.Provider>
  );
}

export default function useUpdateWorker() {
  const context: IUpdateWorkerContext = useContext(UpdateWorkerContext);
  if (context === undefined) {
    throw new Error(
      'useUpdateWorkerReducer must be used within a UpdateWorkerProvider. Wrap a parent component in <UpdateWorkerProvider> to fix this error.'
    );
  }
  return context;
}
